import { captureException } from '@sentry/react';

import type { RawDetection } from './faceFraming';

/**
 * The source is deliberately wider than HTMLVideoElement: MediaPipe accepts
 * any ImageSource, and feeding it a canvas snapshot of the frame is the one
 * way to tell a bad video-element upload apart from bad maths downstream.
 */
export type DetectSource = HTMLVideoElement | HTMLCanvasElement;

export interface FaceDetectorHandle {
  detect(source: DetectSource, timestampMs: number): RawDetection[];
}

/** Copied from node_modules by `copy:mediapipe`; see public/mediapipe/README.md. */
const WASM_PATH = '/mediapipe/wasm';
/** Committed binary — provenance and checksum in public/mediapipe/README.md. */
const MODEL_PATH = '/mediapipe/model.tflite';

/**
 * Session-lifetime singleton. Deliberately never closed: two components share
 * it, so closing on one unmount would break the other, and reference-counting
 * a few megabytes is not worth that failure mode. The camera stream is the
 * resource that actually needs releasing, and each component owns its own.
 */
let detectorPromise: Promise<FaceDetectorHandle | null> | null = null;

async function build(): Promise<FaceDetectorHandle | null> {
  try {
    const { FaceDetector, FilesetResolver } =
      await import('@mediapipe/tasks-vision');
    const fileset = await FilesetResolver.forVisionTasks(WASM_PATH);
    const detector = await FaceDetector.createFromOptions(fileset, {
      baseOptions: { modelAssetPath: MODEL_PATH },
      runningMode: 'VIDEO',
      minDetectionConfidence: 0.5,
    });
    return {
      detect(source, timestampMs) {
        const result = detector.detectForVideo(source, timestampMs);
        return result.detections.map((detection) => ({
          boundingBox: detection.boundingBox,
          keypoints: detection.keypoints.map((k) => ({ x: k.x, y: k.y })),
        }));
      },
    };
  } catch (err) {
    // Blocked wasm, a 404 on the assets, an unsupported browser — none of it
    // may stop the operator from taking a photo. Report and degrade.
    captureException(err);
    return null;
  }
}

export function loadFaceDetector(): Promise<FaceDetectorHandle | null> {
  detectorPromise ??= build();
  return detectorPromise;
}

/** @internal Test seam: the cached detector is module state. */
export function __resetFaceDetectorForTests(): void {
  detectorPromise = null;
}
